import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  Upload, Download, FileSpreadsheet, Users, CheckCircle2, 
  AlertTriangle, Trash2, UserPlus, RefreshCw, ArrowRight,
  FileWarning, Loader2
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const LeadMigration = () => {
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [parsedData, setParsedData] = useState([]);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [executives, setExecutives] = useState([]);
  const [executivesByCity, setExecutivesByCity] = useState({});
  const [stats, setStats] = useState({ total: 0, duplicates: 0, toImport: 0 });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [migrationLogs, setMigrationLogs] = useState([]);

  // Fetch all executives on mount
  useEffect(() => {
    fetchExecutives();
    fetchMigrationLogs();
  }, []);

  const fetchExecutives = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/migration/leads/sales-executives`);
      setExecutives(response.data.executives || []);
      
      // Group by city
      const byCity = {};
      (response.data.executives || []).forEach(exec => {
        (exec.cities || []).forEach(city => {
          const cityLower = city.toLowerCase();
          if (!byCity[cityLower]) byCity[cityLower] = [];
          byCity[cityLower].push(exec);
        });
        // Also add to "all" for execs with no city restriction
        if (!exec.cities || exec.cities.length === 0) {
          if (!byCity['_all']) byCity['_all'] = [];
          byCity['_all'].push(exec);
        }
      });
      setExecutivesByCity(byCity);
    } catch (error) {
      console.error('Failed to fetch executives:', error);
    }
  };

  const fetchMigrationLogs = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/migration/leads/logs?limit=5`);
      setMigrationLogs(response.data.logs || []);
    } catch (error) {
      console.error('Failed to fetch migration logs:', error);
    }
  };

  const downloadTemplate = async (format) => {
    try {
      const response = await axios.get(`${API_URL}/api/migration/leads/download-template?format=${format}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `lead_import_template.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Template downloaded (${format.toUpperCase()})`);
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      const validTypes = ['.xlsx', '.xls', '.csv'];
      const ext = selectedFile.name.toLowerCase().slice(selectedFile.name.lastIndexOf('.'));
      if (!validTypes.includes(ext)) {
        toast.error('Please upload an Excel (.xlsx) or CSV file');
        return;
      }
      setFile(selectedFile);
      setParsedData([]);
      setSelectedRows(new Set());
    }
  };

  const parseFile = async () => {
    if (!file) return;
    
    setParsing(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API_URL}/api/migration/leads/parse-file`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const data = response.data.data || [];
      setParsedData(data);
      updateStats(data);
      toast.success(`Parsed ${data.length} leads from file`);
      
      if (response.data.duplicate_count > 0) {
        toast.warning(`Found ${response.data.duplicate_count} duplicate phone numbers`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to parse file');
    } finally {
      setParsing(false);
    }
  };

  const updateStats = (data) => {
    const total = data.length;
    const duplicates = data.filter(d => d.is_duplicate).length;
    const toImport = data.filter(d => d.include_in_import).length;
    setStats({ total, duplicates, toImport });
  };

  const getExecutivesForCity = (city) => {
    if (!city) return executives;
    const cityLower = city.toLowerCase().trim();
    const cityExecs = executivesByCity[cityLower] || [];
    const allExecs = executivesByCity['_all'] || [];
    const combined = [...cityExecs, ...allExecs];
    // Remove duplicates
    const unique = combined.filter((exec, idx, arr) => 
      arr.findIndex(e => e.id === exec.id) === idx
    );
    return unique.length > 0 ? unique : executives;
  };

  const handleAssignmentChange = (rowId, execId) => {
    const exec = executives.find(e => e.id === execId);
    setParsedData(prev => prev.map(row => 
      row.row_id === rowId 
        ? { ...row, assigned_to: execId, assigned_to_name: exec?.name || '' }
        : row
    ));
  };

  const handleIncludeChange = (rowId, include) => {
    setParsedData(prev => {
      const updated = prev.map(row => 
        row.row_id === rowId ? { ...row, include_in_import: include } : row
      );
      updateStats(updated);
      return updated;
    });
  };

  const toggleSelectRow = (rowId) => {
    setSelectedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
      } else {
        newSet.add(rowId);
      }
      return newSet;
    });
  };

  const selectAll = () => {
    if (selectedRows.size === parsedData.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(parsedData.map(d => d.row_id)));
    }
  };

  const bulkAssignToOne = (execId) => {
    if (selectedRows.size === 0) {
      toast.error('Please select rows first');
      return;
    }
    const exec = executives.find(e => e.id === execId);
    setParsedData(prev => prev.map(row => 
      selectedRows.has(row.row_id)
        ? { ...row, assigned_to: execId, assigned_to_name: exec?.name || '' }
        : row
    ));
    toast.success(`Assigned ${selectedRows.size} leads to ${exec?.name}`);
  };

  const bulkDistributeEvenly = () => {
    if (selectedRows.size === 0) {
      toast.error('Please select rows first');
      return;
    }
    
    // Group selected rows by city
    const rowsByCity = {};
    parsedData.filter(r => selectedRows.has(r.row_id)).forEach(row => {
      const city = row.city?.toLowerCase()?.trim() || '_none';
      if (!rowsByCity[city]) rowsByCity[city] = [];
      rowsByCity[city].push(row.row_id);
    });
    
    // Distribute each city's leads among its executives
    const assignments = {};
    Object.entries(rowsByCity).forEach(([city, rowIds]) => {
      const cityExecs = city === '_none' ? executives : getExecutivesForCity(city);
      if (cityExecs.length === 0) return;
      
      rowIds.forEach((rowId, idx) => {
        const exec = cityExecs[idx % cityExecs.length];
        assignments[rowId] = { assigned_to: exec.id, assigned_to_name: exec.name };
      });
    });
    
    setParsedData(prev => prev.map(row => 
      assignments[row.row_id] 
        ? { ...row, ...assignments[row.row_id] }
        : row
    ));
    
    toast.success(`Distributed ${selectedRows.size} leads evenly among executives`);
  };

  const handleImport = async () => {
    const leadsToImport = parsedData.filter(d => d.include_in_import);
    
    if (leadsToImport.length === 0) {
      toast.error('No leads selected for import');
      return;
    }
    
    setImporting(true);
    try {
      const response = await axios.post(`${API_URL}/api/migration/leads/import`, {
        leads: parsedData
      });
      
      const { imported_count, merged_count, errors_count } = response.data;
      
      if (errors_count > 0) {
        toast.warning(`Imported ${imported_count} leads, merged ${merged_count}, ${errors_count} errors`);
      } else {
        toast.success(`Successfully imported ${imported_count} leads, merged ${merged_count} duplicates`);
      }
      
      // Reset state
      setFile(null);
      setParsedData([]);
      setSelectedRows(new Set());
      setShowConfirmModal(false);
      fetchMigrationLogs();
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="lead-migration-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Lead Migration</h2>
          <p className="text-gray-500 text-sm mt-1">Import leads from your old CRM system</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => downloadTemplate('xlsx')} data-testid="download-xlsx-btn">
            <Download className="w-4 h-4 mr-2" />
            Download Template (.xlsx)
          </Button>
          <Button variant="outline" onClick={() => downloadTemplate('csv')} data-testid="download-csv-btn">
            <Download className="w-4 h-4 mr-2" />
            Download Template (.csv)
          </Button>
        </div>
      </div>

      {/* Upload Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5" />
            Upload Lead File
          </CardTitle>
          <CardDescription>
            Upload an Excel (.xlsx) or CSV file with your leads data
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <Input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileSelect}
                className="cursor-pointer"
                data-testid="file-input"
              />
            </div>
            {file && (
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="px-3 py-1">
                  <FileSpreadsheet className="w-4 h-4 mr-1" />
                  {file.name}
                </Badge>
                <Button onClick={parseFile} disabled={parsing} data-testid="parse-btn">
                  {parsing ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Parsing...</>
                  ) : (
                    <><Upload className="w-4 h-4 mr-2" /> Parse File</>
                  )}
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Stats Summary */}
      {parsedData.length > 0 && (
        <div className="grid grid-cols-4 gap-4">
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-blue-700">{stats.total}</div>
              <div className="text-sm text-blue-600">Total Leads</div>
            </CardContent>
          </Card>
          <Card className="bg-red-50 border-red-200">
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-red-700">{stats.duplicates}</div>
              <div className="text-sm text-red-600">Duplicates Found</div>
            </CardContent>
          </Card>
          <Card className="bg-green-50 border-green-200">
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-green-700">{stats.toImport}</div>
              <div className="text-sm text-green-600">Ready to Import</div>
            </CardContent>
          </Card>
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-amber-700">
                {parsedData.filter(d => d.assigned_to).length}
              </div>
              <div className="text-sm text-amber-600">Assigned</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Bulk Actions */}
      {parsedData.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="w-5 h-5" />
              Bulk Assignment
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Selected: {selectedRows.size}</span>
                <Button variant="outline" size="sm" onClick={selectAll}>
                  {selectedRows.size === parsedData.length ? 'Deselect All' : 'Select All'}
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <Select onValueChange={(val) => bulkAssignToOne(val)} disabled={selectedRows.size === 0}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Assign to one exec..." />
                  </SelectTrigger>
                  <SelectContent>
                    {executives.map(exec => (
                      <SelectItem key={exec.id} value={exec.id}>
                        {exec.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button 
                variant="outline" 
                onClick={bulkDistributeEvenly}
                disabled={selectedRows.size === 0}
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Distribute Evenly by City
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Data Table */}
      {parsedData.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Lead Data Preview</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="max-h-[500px] overflow-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th className="p-3 text-left w-10">
                      <Checkbox 
                        checked={selectedRows.size === parsedData.length}
                        onCheckedChange={selectAll}
                      />
                    </th>
                    <th className="p-3 text-left">Include</th>
                    <th className="p-3 text-left">Name</th>
                    <th className="p-3 text-left">Phone</th>
                    <th className="p-3 text-left">City</th>
                    <th className="p-3 text-left">Status</th>
                    <th className="p-3 text-left">Source</th>
                    <th className="p-3 text-left w-[200px]">Assign To</th>
                    <th className="p-3 text-left">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {parsedData.map((row, idx) => (
                    <tr 
                      key={row.row_id}
                      className={`border-b hover:bg-gray-50 ${
                        row.is_duplicate ? 'bg-red-50' : ''
                      }`}
                    >
                      <td className="p-3">
                        <Checkbox 
                          checked={selectedRows.has(row.row_id)}
                          onCheckedChange={() => toggleSelectRow(row.row_id)}
                        />
                      </td>
                      <td className="p-3">
                        <Checkbox 
                          checked={row.include_in_import}
                          onCheckedChange={(checked) => handleIncludeChange(row.row_id, checked)}
                        />
                        {row.is_duplicate && (
                          <Badge variant="destructive" className="ml-2 text-[10px]">
                            Duplicate
                          </Badge>
                        )}
                      </td>
                      <td className="p-3 font-medium">{row.name}</td>
                      <td className="p-3 text-gray-600">{row.phone}</td>
                      <td className="p-3">{row.city}</td>
                      <td className="p-3">
                        <Badge variant="outline">{row.status}</Badge>
                      </td>
                      <td className="p-3 text-gray-500 text-xs">{row.source}</td>
                      <td className="p-3">
                        <Select 
                          value={row.assigned_to || ''} 
                          onValueChange={(val) => handleAssignmentChange(row.row_id, val)}
                        >
                          <SelectTrigger className="w-full h-8 text-xs">
                            <SelectValue placeholder="Select exec..." />
                          </SelectTrigger>
                          <SelectContent>
                            {getExecutivesForCity(row.city).map(exec => (
                              <SelectItem key={exec.id} value={exec.id}>
                                {exec.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-3 text-gray-500 text-xs max-w-[150px] truncate" title={row.notes}>
                        {row.notes}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Import Button */}
      {parsedData.length > 0 && (
        <div className="flex justify-between items-center">
          <Button 
            variant="outline" 
            onClick={() => {
              setFile(null);
              setParsedData([]);
              setSelectedRows(new Set());
            }}
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Clear All
          </Button>
          <Button 
            size="lg"
            onClick={() => setShowConfirmModal(true)}
            disabled={stats.toImport === 0}
            data-testid="import-btn"
          >
            <ArrowRight className="w-4 h-4 mr-2" />
            Import {stats.toImport} Leads
          </Button>
        </div>
      )}

      {/* Confirm Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-[500px]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                Confirm Import
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                <p><strong>New leads to import:</strong> {stats.toImport - parsedData.filter(d => d.is_duplicate && d.include_in_import).length}</p>
                <p><strong>Duplicates to merge:</strong> {parsedData.filter(d => d.is_duplicate && d.include_in_import).length}</p>
                <p><strong>Leads with assignment:</strong> {parsedData.filter(d => d.include_in_import && d.assigned_to).length}</p>
              </div>
              
              {parsedData.filter(d => d.include_in_import && !d.assigned_to).length > 0 && (
                <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                  <p className="text-sm text-amber-800">
                    {parsedData.filter(d => d.include_in_import && !d.assigned_to).length} leads are not assigned to any sales executive.
                  </p>
                </div>
              )}
              
              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowConfirmModal(false)}>
                  Cancel
                </Button>
                <Button onClick={handleImport} disabled={importing}>
                  {importing ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Importing...</>
                  ) : (
                    <><CheckCircle2 className="w-4 h-4 mr-2" /> Confirm & Import</>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Recent Migrations */}
      {migrationLogs.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Recent Migrations</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {migrationLogs.map((log, idx) => (
                <div key={idx} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <div>
                    <span className="font-medium">{log.imported_count} imported</span>
                    {log.merged_count > 0 && (
                      <span className="text-gray-500 ml-2">+ {log.merged_count} merged</span>
                    )}
                  </div>
                  <div className="text-sm text-gray-500">
                    {new Date(log.imported_at).toLocaleString()} by {log.imported_by}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default LeadMigration;
